/**
 * useDynamicSchema Hook
 *
 * React hook for fetching dynamic schemas from connector APIs.
 * When a parameter has x-ms-dynamic-schema configured, this hook
 * fetches the schema and converts it to additional parameters.
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import type { ConnectionReference } from '@/types/connectionReference';
import type { DynamicSchemaConfig, ParameterInfo } from '@/types/operationParameter';
import {
  fetchDynamicSchema,
  schemaToParameters,
  resolveDynamicSchemaParameters,
  getSchemaDependencyParameterNames,
} from '@/services/dynamicSchemaService';
import type { SchemaObject } from '@/types/valueSegment';
import type { SwaggerParser } from '@/parsers/SwaggerParser';
import { isManagedApiConnector } from '@/utils/connections';
import { useMiAudience } from '@/hooks/useMiAudience';
import { useManagedConnectionInfo } from '@/hooks/useManagedConnectionInfo';
import { useAuth } from '@/auth';
import { isConnectionReferenceComplete } from '@/services/connectionService';

// =============================================================================
// TYPES
// =============================================================================

export interface UseDynamicSchemaOptions {
  /** Dynamic schema configuration from the trigger parameter */
  schemaConfig: DynamicSchemaConfig | undefined;
  /** Connection reference for API calls */
  connectionReference: ConnectionReference | undefined;
  /** Connector ID */
  connectorId: string;
  /** Current values of all parameters (for resolving dependencies) */
  parameterValues: Record<string, unknown>;
  /** Name of the parameter that has the dynamic schema config */
  parentParameterName: string;
  /**
   * Body sub-path under which the resolved children should serialize. Empty
   * or undefined means the parent IS the body root (children serialize
   * directly under `body`). A non-empty value (e.g. `'parameters'` for SQL
   * ExecuteProcedure_V2) prefixes each child key so they serialize under
   * `body.<bodyKeyPrefix>.<name>`. Typically sourced from
   * `parent.info.bodyKeyPath`.
   */
  bodyKeyPrefix?: string | undefined;
  /** Optional node ID for parameter ID generation */
  nodeId?: string;
  /** Whether to auto-fetch when dependencies are satisfied (default: true) */
  autoFetch?: boolean;
  /**
   * Swagger parser for the connector.
   * Required for legacy x-ms-dynamic-schema to look up the target operation.
   */
  swagger?: SwaggerParser | null;
}

export interface UseDynamicSchemaResult {
  /** Generated parameters from the dynamic schema */
  dynamicParameters: ParameterInfo[];
  /** Raw schema from the API (for debugging/advanced use) */
  schema: SchemaObject | null;
  /** Whether schema is currently being fetched */
  isLoading: boolean;
  /** Error message if fetch failed */
  error: string | null;
  /** Whether all required dependencies are satisfied */
  dependenciesSatisfied: boolean;
  /** List of missing dependency parameter names */
  missingDependencies: string[];
  /**
   * A connection reference was supplied but is a placeholder shell (e.g.
   * template-applied with no real connection picked yet). When true the hook
   * suppresses the dynamic-invoke call to avoid 400s / MisMatchingOAuthClaims
   * and the UI should prompt the user to configure the connection.
   */
  missingConnection: boolean;
  /** Manually trigger a refetch */
  refetch: () => void;
}

// =============================================================================
// HOOK IMPLEMENTATION
// =============================================================================

/**
 * Hook for managing dynamic schema fetching and parameter generation.
 *
 * Usage:
 * ```tsx
 * const { dynamicParameters, isLoading, error } = useDynamicSchema({
 *   schemaConfig: parameter.info.dynamicSchema,
 *   connectionReference,
 *   connectorId,
 *   parameterValues: allParameterValues,
 *   accessToken,
 *   parentParameterName: parameter.parameterName,
 * });
 * ```
 */
export function useDynamicSchema({
  schemaConfig,
  connectionReference,
  connectorId,
  parameterValues,
  parentParameterName,
  bodyKeyPrefix,
  nodeId,
  autoFetch = true,
  swagger,
}: UseDynamicSchemaOptions): UseDynamicSchemaResult {
  // State
  const [schema, setSchema] = useState<SchemaObject | null>(null);
  const [dynamicParameters, setDynamicParameters] = useState<ParameterInfo[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { getOttoToken } = useAuth();

  // Returns undefined for non-MI connections; the service branches on auth type.
  const miAudience = useMiAudience(connectorId);
  // Mirrors LogicAppsUX `await getConnection(...)` — `isReady` gates fetch so
  // MI connections don't hit the bare route before classification settles.
  const { isManagedIdentity, connectionRuntimeUrl, isReady } = useManagedConnectionInfo(
    connectorId,
    connectionReference?.connection.id ?? null
  );

  // `connectionReference === undefined` is legitimate (runtime literal-type
  // built-ins like ChunkText invoke /dynamicInvoke without a connection).
  // Only flag as missing when a reference exists but lacks the fields the
  // service layer requires (managed-API connection.id, service-provider
  // connectionProperties/authentication, etc.).
  const missingConnection = useMemo(
    () => connectionReference !== undefined && !isConnectionReferenceComplete(connectionReference),
    [connectionReference]
  );

  // Ref to track last fetch parameters (for deduplication)
  const lastFetchParamsRef = useRef<string | null>(null);

  // Get dependency info
  const dependencyNames = useMemo(() => {
    if (!schemaConfig) return [];
    return getSchemaDependencyParameterNames(schemaConfig);
  }, [schemaConfig]);

  // Build swagger-based parameter name mapping for resolving x-ms-summary variations
  // e.g., "PostAs" (from x-ms-summary "Post as") → "poster" (canonical param.name)
  const swaggerMapping = useMemo(() => {
    if (!swagger) {
      return undefined;
    }
    const mapping = swagger.buildGlobalParameterNameMapping();
    return mapping;
  }, [swagger]);

  // Build the set of dynamic-schema dependency references that are OPTIONAL on
  // the current operation (i.e. swagger `required: false`). Mirrors
  // LogicAppsUX's `parameterValidForDynamicCall`: only required dependencies
  // block the dynamicInvoke call — optional ones (e.g. SharePoint `view` on
  // PostItem/PatchItem) get omitted from the request when unset so columns
  // still load with the default view's schema.
  const optionalDependencyRefs = useMemo(() => {
    if (!swagger || !schemaConfig?.parameters) {
      return undefined;
    }
    const currentOperationId = parameterValues['_operationId'];
    if (typeof currentOperationId !== 'string') {
      return undefined;
    }
    const op = swagger.getOperationByOperationId(currentOperationId);
    if (!op?.operation.parameters) {
      return undefined;
    }
    // Lower-cased param name → required flag
    const requiredByName = new Map<string, boolean>();
    for (const p of op.operation.parameters) {
      requiredByName.set(p.name.toLowerCase(), p.required === true);
    }
    const optional = new Set<string>();
    for (const cfg of Object.values(schemaConfig.parameters)) {
      const ref = cfg.parameterReference;
      if (!ref) continue;
      const refLower = ref.toLowerCase();
      const required = requiredByName.get(refLower);
      // If swagger has the param and it's not required, treat as optional.
      if (required === false) {
        optional.add(refLower);
      }
    }
    return optional;
  }, [swagger, schemaConfig, parameterValues]);

  // Extract path parameter values from _path for swagger operations
  // Path like "/v1.0/.../poster/\${encodeURIComponent('User')}/location/\${encodeURIComponent('Channel')}"
  // Also handles legacy @{...} syntax from existing workflow definitions
  const enrichedParameterValues = useMemo(() => {
    const result = { ...parameterValues };
    const pathValue = parameterValues['_path'];

    if (typeof pathValue === 'string' && (pathValue.includes('${encodeURIComponent(') || pathValue.includes('@{encodeURIComponent('))) {
      // Extract path parameter values using pattern: /paramName/\${encodeURIComponent('value')} or @{...}
      const pathParamPattern = /\/([a-zA-Z]+)\/[@$]\{encodeURIComponent\('([^']+)'\)\}/g;
      let match;
      while ((match = pathParamPattern.exec(pathValue)) !== null) {
        const [, paramName, paramValue] = match;
        if (paramName && paramValue && !(paramName in result)) {
          result[paramName] = paramValue;
        }
      }
    }

    return result;
  }, [parameterValues]);

  // Check if dependencies are satisfied
  const { dependenciesSatisfied, missingDependencies, resolvedParams } = useMemo(() => {
    if (!schemaConfig) {
      return { dependenciesSatisfied: true, missingDependencies: [] as string[], resolvedParams: null };
    }

    // Use swagger mapping to resolve x-ms-summary variations
    const resolved = resolveDynamicSchemaParameters(
      schemaConfig,
      enrichedParameterValues,
      swaggerMapping,
      optionalDependencyRefs,
    );
    const missing = dependencyNames.filter((name) => {
      // Optional dependencies never count as "missing" — they're allowed to be unset.
      if (optionalDependencyRefs?.has(name.toLowerCase())) {
        return false;
      }
      // Try direct lookup first (using enriched values with path params)
      let value = enrichedParameterValues[name];

      // Case-insensitive fallback
      if (value === undefined) {
        const lowerName = name.toLowerCase();
        for (const [key, val] of Object.entries(enrichedParameterValues)) {
          if (key.toLowerCase() === lowerName) {
            value = val;
            break;
          }
        }
      }

      // Use swagger mapping as final fallback
      if (value === undefined && swaggerMapping) {
        const mappedName = swaggerMapping.get(name.toLowerCase());
        if (mappedName) {
          value = enrichedParameterValues[mappedName];
          if (value === undefined) {
            // Try case-insensitive lookup with mapped name
            for (const [key, val] of Object.entries(enrichedParameterValues)) {
              if (key.toLowerCase() === mappedName.toLowerCase()) {
                value = val;
                break;
              }
            }
          }
        }
      }

      return value === undefined || value === null || value === '';
    });
    return {
      dependenciesSatisfied: resolved !== null,
      missingDependencies: missing,
      resolvedParams: resolved,
    };
  }, [schemaConfig, enrichedParameterValues, dependencyNames, swaggerMapping, optionalDependencyRefs]);

  // Fetch schema function
  const fetchSchema = useCallback(async () => {
    // Validate preconditions. `connectionReference` is optional — runtime
    // literal-type built-ins (e.g. ChunkText) invoke `dynamicInvoke` without a
    // connection, so absence is legitimate. But a *placeholder* reference
    // (template-applied shell with no real connection picked) is not — the
    // service would 400 with MisMatchingOAuthClaims.
    if (!schemaConfig || !resolvedParams || missingConnection) {
      return;
    }

    // `isReady` blocks fire until the connection ARM has settled (LogicAppsUX `await getConnection` parity).
    // Gated here so both auto-fetch and manual `refetch()` honor the same contract.
    if (!isReady) {
      return;
    }

    // For managed connectors with legacy x-ms-dynamic-schema, swagger is required
    const isManagedConnector = isManagedApiConnector(connectorId);
    if (isManagedConnector && !swagger) {
      console.warn('[useDynamicSchema] Swagger required for legacy dynamic schema but not provided');
      return;
    }

    // dynamicSchemaService routes both managed connectors and service providers
    // through the Otto control plane (`getLogicEndpoint()`), never ARM, so an
    // Otto-audience token is required — ARM tokens produce `MisMatchingOAuthClaims`.
    const accessToken = await getOttoToken();

    if (!accessToken) {
      setError('Failed to acquire access token for fetching dynamic schema');
      return;
    }
    
    // miAudience / isManagedIdentity / connectionRuntimeUrl are in the key so
    // MI connections re-fetch once detection settles.
    const fetchParamsKey = JSON.stringify({
      operationId: schemaConfig.operationId,
      connectionId: connectionReference?.connection.id ?? connectorId,
      params: resolvedParams,
      miAudience: miAudience ?? '',
      isManagedIdentity,
      connectionRuntimeUrl: connectionRuntimeUrl ?? '',
    });

    // Skip if same params as last fetch (don't retry on error to avoid infinite loop)
    if (fetchParamsKey === lastFetchParamsRef.current) {
      return;
    }

    lastFetchParamsRef.current = fetchParamsKey;
    setIsLoading(true);
    setError(null);

    try {
      const result = await fetchDynamicSchema({
        connectionReference,
        connectorId,
        schemaConfig,
        resolvedParameters: resolvedParams,
        accessToken,
        swagger: swagger ?? undefined,
        ...(miAudience ? { miAudience } : {}),
        isManagedIdentity,
        ...(connectionRuntimeUrl ? { connectionRuntimeUrl } : {}),
      });

      if (result.error) {
        setError(result.error);
        setSchema(null);
        setDynamicParameters([]);
      } else if (result.schema) {
        // Convert schema to parameters
        const params = schemaToParameters(result.schema, parentParameterName, nodeId, bodyKeyPrefix);
        setSchema(result.schema);
        setDynamicParameters(params);
        setError(null);
      } else {
        setSchema(null);
        setDynamicParameters([]);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch dynamic schema';
      setError(message);
      setSchema(null);
      setDynamicParameters([]);
    } finally {
      setIsLoading(false);
    }
  }, [schemaConfig, connectionReference, resolvedParams, connectorId, swagger, getOttoToken, parentParameterName, bodyKeyPrefix, nodeId, miAudience, isManagedIdentity, connectionRuntimeUrl, isReady, missingConnection]);

  // Auto-fetch when dependencies change
  useEffect(() => {
    // `isReady` blocks fire until the connection ARM has settled (LogicAppsUX `await getConnection` parity).
    if (!autoFetch || !dependenciesSatisfied || missingConnection || !isReady) {
      return;
    }

    fetchSchema();
  }, [autoFetch, dependenciesSatisfied, missingConnection, isReady, fetchSchema]);

  // Clear parameters when dependencies become unsatisfied or the connection
  // reference disappears / becomes a placeholder shell.
  useEffect(() => {
    if ((!dependenciesSatisfied || missingConnection) && dynamicParameters.length > 0) {
      setDynamicParameters([]);
      setSchema(null);
      lastFetchParamsRef.current = null;
    }
  }, [dependenciesSatisfied, missingConnection, dynamicParameters.length]);

  // Manual refetch function
  const refetch = useCallback(() => {
    lastFetchParamsRef.current = null; // Force refetch
    fetchSchema();
  }, [fetchSchema]);

  return {
    dynamicParameters,
    schema,
    isLoading,
    error,
    dependenciesSatisfied,
    missingDependencies,
    missingConnection,
    refetch,
  };
}

// =============================================================================
// UTILITY HOOKS
// =============================================================================

/**
 * Hook to check if any parameter in a list has dynamic schema configuration.
 * Useful for optimizing renders when no dynamic schemas are present.
 */
export function useHasDynamicSchemaParameters(parameters: ParameterInfo[]): boolean {
  return useMemo(
    () => parameters.some((param) => param.info?.dynamicSchema !== undefined),
    [parameters]
  );
}

/**
 * Hook to get all parameters that have dynamic schema configurations.
 */
export function useDynamicSchemaParameters(parameters: ParameterInfo[]): ParameterInfo[] {
  return useMemo(
    () => parameters.filter((param) => param.info?.dynamicSchema !== undefined),
    [parameters]
  );
}
